import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Filter, Loader, PlusIcon, SendIcon, UserIcon, UsersRoundIcon } from "lucide-react";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';
import { ChatType, fetchChatsPage, useChatsPaginated, ChatStream, ChatsParams, ChatTypeOptions, ChatSourceOptions, MessageGroupOptions, useSendTestMessage, TwilioMessageName, SendTestMessageValidation } from "@/lib/api/chats";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatList } from "./ChatList";
import { ChatDetail } from "./ChatDetail";
import { CreateChatStream } from "./ManageChatStream";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { BearInput, BearSelectInput } from "@/lib/form/ui";
import { useQueryParam } from "@/hooks/use-query-param";
import { findByKey } from "@/lib/prelude";
import { convertStreamTypeToChatType } from "./utils";
import { GroupChatSection } from "./GroupChatSection";
import { TestMessageModal } from "./TestMessageDialog";

export function ChatDashboard() {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [streamType, setStreamType] = useQueryParam('stream_type', 'individual');
  const filtersApplied = useRef(false);

  const handleOnStreamTypeChange = (base_type: string) => () => setStreamType(base_type);

  return (
    <div className="flex flex-col h-screen max-h-[calc(100vh-6rem)]">
      <div className=" mb-2 ">
        <span className="flex flex-row justify-between items-center">
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            Chats
          </h1>
          <span className="flex flex-row">
            <TestMessageModal />
            {streamType === 'group' && <CreateChatStream />}
            <Button onClick={handleOnStreamTypeChange("group")} variant="outline" className={cn(streamType === 'group' ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : '', 'gap-2 mr-2 ')}>
              <UsersRoundIcon className="w-4 h-4" />
            </Button>
            <Button onClick={handleOnStreamTypeChange("individual")} variant="outline" className={cn(streamType === 'individual' ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : '', 'gap-2 mr-2 ')}>
              <UserIcon className="w-4 h-4" />
            </Button>
          </span>
        </span>
      </div>
      <>
        {streamType === 'individual' && (
          <IndividualChatSection />
        )}
        {streamType === 'group' && (
          <GroupChatSection />
        )}
      </>
    </div>
  );
}


function IndividualChatSection() {
  const [temporaryChatParams, setTemporaryChatParams] = useState<Omit<ChatsParams, "page">>({});
  const [page, setPage] = useState(1);
  const [selectedChat, setSelectedChat] = useState<ChatStream | null>(null);
  const [chatParams, setChatParams] = useState<Omit<ChatsParams, "page">>({});
  const chatsRes: any = useChatsPaginated({
    params: { ...chatParams, page, chatType: convertStreamTypeToChatType('individual'), pageSize: 10 },
    queryFn: () => fetchChatsPage({ ...chatParams, page, chatType: convertStreamTypeToChatType('individual'), pageSize: 10 })
  });

  const chatData = chatsRes?.data?.data || {};
  const chatItems = chatData.data || [];
  const pagination = chatData.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  };

  return (
    <div className="flex flex-col flex-grow h-[calc(100%-3rem)] md:grid md:grid-cols-[minmax(300px,400px),1fr] gap-4">
      <div className={cn("w-full h-full border rounded-lg overflow-hidden flex flex-col", !selectedChat ? "" : "hidden md:block")}>
        <ChatList
          chats={chatItems}
          pagination={pagination}
          isLoading={chatsRes.isFetching}
          onPageChange={setPage}
          onSelectChat={setSelectedChat}
          selectedChatId={selectedChat?.id || null}
        />
      </div>
      <div className={cn("w-full h-full border rounded-lg overflow-hidden flex flex-col", !selectedChat ? "hidden md:block" : "")}>
        {selectedChat ? (
          <ChatDetail
            chat={selectedChat}
            onBackClick={() => setSelectedChat(null)}
          />
        ) : (
          <div className={cn("flex items-center justify-center h-full text-muted-foreground")}>
            Select a chat to view the conversation
          </div>
        )}
      </div>
    </div>
  );
}
